import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import { TopNavBar } from './shared/TopNavBar';
import { API_BASE } from './config';

interface ImageItem {
  url?: string;
  width?: number;
  height?: number;
  [key: string]: unknown;
}

interface VideoResult {
  url?: string;
  width?: number;
  height?: number;
  poster?: string;
  definition?: string;
  [key: string]: unknown;
}

const DoubaoPage: React.FC = () => {
  const [tab, setTab] = useState<0 | 1>(0);
  const [inputUrl, setInputUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [images, setImages] = useState<ImageItem[]>([]);
  const [video, setVideo] = useState<VideoResult | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const token = localStorage.getItem('access_token');

  const handleParse = async () => {
    if (!inputUrl.trim()) return;
    setLoading(true);
    setError('');
    setImages([]);
    setVideo(null);

    const endpoint = tab === 0 ? '/api/doubao/parse-image' : '/api/doubao/parse-video';

    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ url: inputUrl.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '解析失败');
      if (tab === 0) {
        setImages(data.images || []);
      } else {
        setVideo(data);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '未知错误');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (url: string) => {
    await navigator.clipboard.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 2000);
  };

  const downloadFile = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eff6ff' }}>
      <TopNavBar />

      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, md: 4 }, pt: 6, pb: 8 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', mb: 1 }}
          >
            豆包 AI 去水印
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            粘贴豆包分享链接，一键提取无水印图片或视频
          </Typography>
        </Box>

        {/* Input card */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #bfdbfe',
            borderRadius: 3,
            p: 3,
            bgcolor: 'white',
            mb: 3,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setImages([]); setVideo(null); setError(''); }}
            sx={{ mb: 2.5, '& .MuiTab-root': { fontWeight: 700, minWidth: 100 } }}
          >
            <Tab icon={<ImageSearchIcon />} iconPosition="start" label="提取图片" />
            <Tab icon={<OndemandVideoIcon />} iconPosition="start" label="提取视频" />
          </Tabs>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={
                tab === 0
                  ? '粘贴豆包图片分享链接，例如 https://www.doubao.com/share/...'
                  : '粘贴豆包视频分享链接，例如 https://www.doubao.com/share/...'
              }
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleParse()}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.9rem',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleParse}
              disabled={loading || !inputUrl.trim()}
              sx={{
                minWidth: 100,
                borderRadius: 2,
                fontWeight: 700,
                bgcolor: '#2563eb',
                '&:hover': { bgcolor: '#1d4ed8' },
                flexShrink: 0,
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : '解析'}
            </Button>
          </Box>

          {error && (
            <Typography sx={{ color: '#ef4444', mt: 1.5, fontSize: '0.875rem' }}>
              {error}
            </Typography>
          )}
        </Paper>

        {/* Image results */}
        {tab === 0 && images.length > 0 && (
          <Paper
            elevation={0}
            sx={{ border: '1px solid #bfdbfe', borderRadius: 3, p: 3, bgcolor: 'white' }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
              共找到 {images.length} 张图片
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 2,
              }}
            >
              {images.map((img, i) => {
                const url = img.url || (typeof img === 'string' ? img : '');
                return (
                  <Box
                    key={i}
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid #e2e8f0',
                      bgcolor: '#f8fafc',
                      aspectRatio: '1',
                      '&:hover .actions': { opacity: 1 },
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={`图片 ${i + 1}`}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    {/* Hover actions */}
                    <Box
                      className="actions"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(0,0,0,0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      <Tooltip title={copied === url ? '已复制!' : '复制链接'}>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(url)}
                          sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#f1f5f9' } }}
                        >
                          <ContentCopyIcon fontSize="small" sx={{ color: '#2563eb' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="下载图片">
                        <IconButton
                          size="small"
                          onClick={() => downloadFile(url, `doubao_image_${i + 1}.jpg`)}
                          sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#f1f5f9' } }}
                        >
                          <DownloadIcon fontSize="small" sx={{ color: '#16a34a' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {/* Index badge */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 6,
                        left: 6,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        px: 0.8,
                        py: 0.2,
                        borderRadius: 1,
                      }}
                    >
                      {i + 1}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Video result */}
        {tab === 1 && video && video.url && (
          <Paper
            elevation={0}
            sx={{ border: '1px solid #bfdbfe', borderRadius: 3, p: 3, bgcolor: 'white' }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
              视频解析成功
              {video.width && video.height ? ` · ${video.width}×${video.height}` : ''}
              {video.definition ? ` · ${video.definition}` : ''}
            </Typography>
            <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2, bgcolor: '#000' }}>
              <video
                controls
                poster={video.poster}
                style={{ width: '100%', maxHeight: 480, display: 'block' }}
              >
                <source src={video.url} />
              </video>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={() => copyToClipboard(video.url!)}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                {copied === video.url ? '已复制!' : '复制视频链接'}
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => downloadFile(video.url!, 'doubao_video.mp4')}
                sx={{ borderRadius: 2, fontWeight: 600, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
              >
                下载视频
              </Button>
            </Box>
          </Paper>
        )}

        {/* Empty state */}
        {!loading && images.length === 0 && !video && !error && (
          <Box sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
            <Box sx={{ fontSize: 56, mb: 2 }}>🎯</Box>
            <Typography sx={{ fontWeight: 600 }}>粘贴豆包分享链接后点击解析</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              支持图片和视频，去除水印后直接下载
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DoubaoPage;
